import {
  dereferenceJSONSchemaDraft04,
  dereferenceJSONSchemaDraft06,
  dereferenceJSONSchemaDraft07,
  dereferenceJSONSchemaDraft2020_12
} from '@criteria/json-schema'
import { metaSchemaURI as draft04MetaSchemaURI } from '@criteria/json-schema/draft-04'
import { metaSchemaURI as draft06MetaSchemaURI } from '@criteria/json-schema/draft-06'
import { metaSchemaURI as draft07MetaSchemaURI } from '@criteria/json-schema/draft-07'
import { metaSchemaURI as draft2020_12MetaSchemaURI } from '@criteria/json-schema/draft-2020-12'
import { useSyncExternalStore } from 'react'
import { invariant } from '../lib/invariant'
import { DocumentStore } from './DocumentStore'
import { JSONDocument, JSONDocumentFormat } from './JSONDocument'

export function useJSONSchemaDocumentJSONSchema(document: JSONSchemaDocument) {
  return useSyncExternalStore(
    (callback) => {
      document.addListener(callback)
      return () => document.removeListener(callback)
    },
    () => {
      document.reloadContent().catch(console.error)
      return document.jsonSchema
    }
  )
}

export function useJSONSchemaDocumentJSONSchemaWarnings(document: JSONSchemaDocument) {
  return useSyncExternalStore(
    (callback) => {
      document.addListener(callback)
      return () => document.removeListener(callback)
    },
    () => document.jsonSchemaWarnings
  )
}

export class JSONSchemaDocument extends JSONDocument {
  constructor(store: DocumentStore, path: string, fileHandle: FileSystemFileHandle, format: JSONDocumentFormat) {
    super(store, path, fileHandle, format)
  }

  jsonSchema: object | boolean | undefined
  jsonSchemaError: Error | undefined
  jsonSchemaWarnings: { message: string }[] = []

  override async updateText(text: string) {
    this.jsonSchema = undefined
    this.jsonSchemaError = undefined
    this.jsonSchemaWarnings = []
    await super.updateText(text)
  }

  override error(): Error | undefined {
    return this.jsonSchemaError ?? super.error()
  }

  override async reloadContent() {
    if (this.jsonSchema !== undefined || this.error() !== undefined) {
      return
    }

    await super.reloadContent()
    if (this.data === undefined) {
      return
    }

    this.store.removeAllDependencies(this)

    if (typeof this.data !== 'object' || this.data === null) {
      this.jsonSchema = undefined
      this.jsonSchemaWarnings = []
      this.jsonSchemaError = new Error(`The document is not a valid ${this.format.toUpperCase()} object.`)
      this.dispatchChange()
      return
    }

    const warnings: { message: string }[] = []

    const retrieve = async (uri: string) => {
      if (!uri.startsWith('playground://')) {
        warnings.push({ message: `Unable to retrieve document at '${uri}'` })
        return {}
      }

      const referencedPath = uri.slice('playground://'.length)
      if (referencedPath === this.path) {
        return this.data
      }

      this.store.addDependency(this, referencedPath)

      let referencedDocument = await this.store.getDocuments()[referencedPath]
      if (!referencedDocument || !(referencedDocument instanceof JSONDocument)) {
        warnings.push({ message: `Unable to retrieve document at '${referencedPath}'` })
        return {}
      }

      await referencedDocument.reloadContent()
      const referencedDocumentError = referencedDocument.jsonError ?? referencedDocument.fileError
      if (referencedDocumentError) {
        warnings.push(referencedDocumentError)
        return {}
      }

      return referencedDocument.data
    }

    const dereferenceOptions = {
      baseURI: 'playground://' + this.path,
      retrieve
    }

    try {
      const schema = '$schema' in this.data && typeof this.data.$schema === 'string' ? this.data.$schema : null
      switch (schema) {
        case draft04MetaSchemaURI: {
          this.jsonSchema = dereferenceJSONSchemaDraft04(this.data, dereferenceOptions)
          this.jsonSchemaWarnings = warnings
          this.jsonSchemaError = undefined
        }
        case draft06MetaSchemaURI: {
          this.jsonSchema = dereferenceJSONSchemaDraft06(this.data, dereferenceOptions)
          this.jsonSchemaWarnings = warnings
          this.jsonSchemaError = undefined
        }
        case draft07MetaSchemaURI: {
          this.jsonSchema = dereferenceJSONSchemaDraft07(this.data, dereferenceOptions)
          this.jsonSchemaWarnings = warnings
          this.jsonSchemaError = undefined
        }
        case draft2020_12MetaSchemaURI:
        default: {
          this.jsonSchema = dereferenceJSONSchemaDraft2020_12(this.data, dereferenceOptions)
          this.jsonSchemaWarnings = warnings
          this.jsonSchemaError = undefined
        }
      }
    } catch (error) {
      this.jsonSchema = undefined
      this.jsonSchemaWarnings = []
      this.jsonSchemaError = new Error('The document is not a valid JSON Schema document', { cause: error })
    }

    this.dispatchChange()
  }

  onDependencyChange() {
    this.jsonSchema = undefined
    this.jsonSchemaWarnings = []
    this.jsonSchemaError = undefined

    this.reloadContent()
  }

  static isOpenAPIData(data: any) {
    return (
      typeof data === 'object' &&
      data !== null &&
      // infer JSON Schema if any core vocabulary keywords are present, or type is present
      ('$schema' in data ||
        '$vocabulary' in data ||
        '$id' in data ||
        '$anchor' in data ||
        '$dynamicAnchor' in data ||
        '$ref' in data ||
        '$dynamicRef' in data ||
        '$defs' in data ||
        '$comment' in data ||
        'id' in data ||
        'definitions' in data ||
        'type' in data)
    )
  }

  static makeJSONSchemaDocument(document: JSONDocument) {
    if (document instanceof JSONSchemaDocument) {
      return
    }
    Object.setPrototypeOf(document, JSONSchemaDocument.prototype)
    Object.assign(document, { jsonSchema: undefined, jsonSchemaWarnings: [] } as Pick<
      JSONSchemaDocument,
      'jsonSchema' | 'jsonSchemaWarnings'
    >)
    document.dispatchChange()
  }
}
